import React, { useEffect, useState } from "react";
import { api, fmtNum, curSym, errMsg } from "@finance/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Textarea } from "@finance/components/ui/textarea";
import { Switch } from "@finance/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { SearchableSelect } from "@finance/components/SearchableSelect";
import { QuickAddButton } from "@finance/components/QuickAdd";
import { CurrencySelect } from "@finance/components/CurrencySelect";
import { Trash, Plus } from "@phosphor-icons/react";
import { DocNumberField } from "@finance/components/DocNumberField";
import { DocumentPreview } from "@finance/components/DocumentPreview";
import { useStatusFlow } from "@finance/lib/useStatusFlow";

const emptyItem = () => ({ description: "", qty: 1, unit_price: 0, exclusive_amount: 0, tax_type: "Vatable", stocking: false, cost_only: false, client_po_no: "", client_po_id: null });

export default function TransactionForm({ open, onOpenChange, initial, clients, suppliers, users, onSaved, asPage = false }) {
  const [f, setF] = useState({});
  const STATUS_FLOW = useStatusFlow();
  const [items, setItems] = useState([]);
  const [inv, setInv] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [clientPos, setClientPos] = useState([]);
  const [poDetails, setPoDetails] = useState({}); // { [poId]: [ {line_index, sku, item_id, description, unit_price, qty_remaining} ] }
  const [autofillType, setAutofillType] = useState("Purchase_Order");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [autofillDocId, setAutofillDocId] = useState(null);

  const directSale = !!f.direct_sale;
  const availBySku = React.useMemo(() => {
    const m = {};
    (inv || []).forEach((r) => { if (r.sku) m[r.sku] = r.available ?? 0; });
    return m;
  }, [inv]);

  useEffect(() => {
    if (!open) return;
    api.get("/items").then((r) => setItems(r.data)).catch(() => {});
    api.get("/client-pos").then((r) => setClientPos(r.data)).catch(() => {});
    api.get("/inventory").then((r) => setInv(r.data)).catch(() => {});
    setClientList(clients || []);
    setSupplierList(suppliers || []);
    setAutofillDocId(null);
    setF(
      initial
        ? { ...initial, line_items: initial.line_items?.length ? initial.line_items : [emptyItem()] }
        : {
            currency: "TZS", vat_rate: 18, status: "Quote Requested", is_import: false,
            holds_stock: false, line_items: [emptyItem()],
          }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const loadPoDetail = React.useCallback(async (poId) => {
    if (!poId) return;
    try {
      const params = initial?.id ? { exclude_txn: initial.id } : {};
      const r = await api.get(`/client-pos/${poId}`, { params });
      setPoDetails((s) => ({ ...s, [poId]: r.data.lines || [] }));
    } catch { /* ignore */ }
  }, [initial]);

  // Prefetch PO detail for any line that already references a client PO (e.g. when editing)
  useEffect(() => {
    if (!open) return;
    const ids = new Set((f.line_items || []).map((it) => it.client_po_id).filter(Boolean));
    ids.forEach((id) => { if (!poDetails[id]) loadPoDetail(id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, f.line_items, loadPoDetail]);

  // SKUs allowed on a procuring line = only the items on its selected Client PO
  const skuOptionsFor = (it) => {
    const pol = (it.client_po_id && poDetails[it.client_po_id]) || [];
    return pol.filter((l) => (l.sku || l.item_id) && (l.qty_remaining || 0) > 0).map((l) => {
      const im = items.find((x) => (l.item_id && x.id === l.item_id) || (l.sku && x.sku === l.sku));
      return { value: im?.id || l.item_id || l.sku, label: `${l.sku || im?.sku || ""} · ${l.description || im?.name || ""} · ${l.qty_remaining} left`, poLine: l, im };
    });
  };
  const remainingFor = (it) => {
    const pol = (it.client_po_id && poDetails[it.client_po_id]) || [];
    const l = pol.find((x) => (it.item_id && (x.item_id === it.item_id)) || (it.sku && x.sku === it.sku));
    return l ? l.qty_remaining : null;
  };

  useEffect(() => {
    if (f.currency && f.currency !== "TZS" && !f.exchange_rate) {
      api.get("/currencies").then((r) => {
        const c = r.data.find((x) => x.code === f.currency);
        if (c?.latest_rate) setF((s) => ({ ...s, exchange_rate: c.latest_rate }));
      }).catch(() => {});
    }
  }, [f.currency]);

  const autofill = async (file) => {
    if (!file) return;
    setUploadingDoc(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("document_type", autofillType); fd.append("run_extraction", "true");
    try {
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ex = data.ocr_extracted_fields || {};
      setF((s) => {
        const next = { ...s };
        ["lx_po_no", "currency", "supplier_pfi_no", "supplier_pfi_date", "supplier_tax_invoice_no", "supplier_tax_invoice_date", "supplier_efd_qr_code", "supplier_efd_time", "supplier_delivery_note_no", "commercial_invoice_no", "bl_awb_no"].forEach((k) => { if (ex[k] != null && ex[k] !== "") next[k] = ex[k]; });
        if (Array.isArray(ex.line_items) && ex.line_items.length) {
          next.line_items = ex.line_items.map((it) => ({ description: it.description || "", qty: Number(it.qty) || 0, unit_price: Number(it.unit_price) || 0, exclusive_amount: Number(it.exclusive_amount) || (Number(it.qty) || 0) * (Number(it.unit_price) || 0) }));
        }
        return next;
      });
      setAutofillDocId(data.id);
      if (data.ocr_error) toast.warning("Couldn't read the document — please fill manually.");
      else toast.success("Fields auto-filled from document. Review before saving.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploadingDoc(false); }
  };

  const setItem = (i, k, v) => {
    const items = [...f.line_items];
    items[i] = { ...items[i], [k]: v };
    if (k === "qty" || k === "unit_price") {
      items[i].exclusive_amount = (Number(items[i].qty) || 0) * (Number(items[i].unit_price) || 0);
    }
    set("line_items", items);
  };

  const exclusiveTotal = (f.line_items || []).reduce((s, it) => s + (Number(it.exclusive_amount) || 0), 0);
  const _types = new Set((f.line_items || []).filter((it) => it.sku).map((it) => (items.find((x) => x.sku === it.sku)?.item_type) || "Goods"));
  const poKind = _types.size > 1 ? "MIXED" : _types.has("Service") ? "SERVICE" : "GOODS";
  const vatableTotal = (f.line_items || []).filter((it) => (it.tax_type || "Vatable") === "Vatable").reduce((s, it) => s + (Number(it.exclusive_amount) || 0), 0);
  const vatAmount = vatableTotal * 0.18;
  const total = exclusiveTotal + vatAmount;
  const [preview, setPreview] = useState(false);

  const save = async () => {
    if (directSale) {
      if (!f.client_id || f.client_id === "none") { toast.error("Select a client for this sale."); return false; }
      if ((f.line_items || []).some((it) => !it.sku)) { toast.error("Pick a SKU for every line so stock and margin can be tracked."); return false; }
      const over = [];
      (f.line_items || []).forEach((it) => {
        if (!it.sku) return;
        const avail = availBySku[it.sku] ?? 0;
        if ((Number(it.qty) || 0) > avail + 0.001) over.push(`${it.sku} (need ${Number(it.qty) || 0}, have ${avail})`);
      });
      if (over.length) { toast.error(`Not enough stock: ${over.join("; ")}`); return false; }
    }
    const payload = {
      ...f,
      vat_rate: Number(f.vat_rate) || 18,
      exchange_rate: f.exchange_rate ? Number(f.exchange_rate) : null,
      freight_charges: f.freight_charges ? Number(f.freight_charges) : null,
      customs_duty: f.customs_duty ? Number(f.customs_duty) : null,
      withholding_tax: f.withholding_tax ? Number(f.withholding_tax) : null,
      payable_amount: f.payable_amount ? Number(f.payable_amount) : null,
      line_items: (f.line_items || []).map((it) => ({
        description: it.description, qty: Number(it.qty) || 0,
        unit_price: Number(it.unit_price) || 0, exclusive_amount: Number(it.exclusive_amount) || 0,
        tax_type: it.tax_type || "Vatable",
        cost_only: directSale ? false : !!it.cost_only,
        item_id: it.cost_only ? null : (it.item_id || null), sku: it.cost_only ? null : (it.sku || null),
        stocking: (directSale || it.cost_only) ? false : !!it.stocking,
        client_po_no: (directSale || it.stocking || it.cost_only) ? null : (it.client_po_no || null),
        client_po_id: (directSale || it.stocking || it.cost_only) ? null : (it.client_po_id || null),
      })),
      client_id: f.client_id === "none" ? null : f.client_id,
      supplier_id: directSale ? null : (f.supplier_id === "none" ? null : f.supplier_id),
      assigned_to: f.assigned_to === "none" ? null : f.assigned_to,
    };
    try {
      let res;
      if (initial?.id) res = await api.put(`/transactions/${initial.id}`, payload);
      else res = await api.post("/transactions", payload);
      if (autofillDocId && res?.data?.id) {
        try { await api.post(`/documents/${autofillDocId}/confirm`, { transaction_id: res.data.id }); } catch {}
      }
      toast.success("Transaction saved");
      onOpenChange(false); onSaved?.(res?.data);
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };

  const title = directSale ? "Sell from Stock" : (initial?.id ? "Edit Transaction" : "New Transaction");
  const footer = (
    <>
      <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
      <Button variant="outline" data-testid="preview-txn" onClick={() => setPreview(true)}>Preview</Button>
      <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-txn" onClick={save}>Save Transaction</Button>
    </>
  );
  const body = (
      <>
        {!directSale && (
        <div className="mb-2 rounded-sm border border-dashed border-gray-300 bg-gray-50 p-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-gray-600"><strong>Auto-fill from a document (OCR)</strong> — upload a PO/invoice photo and we'll read the fields. Or just fill in manually below.</div>
            <div className="flex items-center gap-2">
              <Select value={autofillType} onValueChange={setAutofillType}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="autofill-doctype"><SelectValue /></SelectTrigger>
                <SelectContent>{["Purchase_Order", "Supplier_PFI", "Supplier_Tax_Invoice", "Delivery_Note"].map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
              <label className="inline-flex cursor-pointer items-center gap-1 rounded-sm bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-700" data-testid="autofill-upload">
                {uploadingDoc ? "Reading…" : "Upload"}
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => autofill(e.target.files[0])} />
              </label>
            </div>
          </div>
        </div>
        )}

        <div className="grid grid-cols-2 gap-x-4 gap-y-4">
          <div className="col-span-2 border-b border-gray-100 pb-1 lx-label">Parties &amp; References</div>
          <DocNumberField docKey="lynx_po" label="Somcore PO No" testid="txn-lx-po" value={f.lx_po_no} onChange={(v) => set("lx_po_no", v)} />
          {directSale && (
          <Field label="Client">
            <div className="flex items-center gap-2">
              <SearchableSelect
                testid="txn-client"
                placeholder="Select client"
                value={f.client_id || "none"}
                onValueChange={(v) => set("client_id", v)}
                options={[{ value: "none", label: "— None —" }, ...clientList.map((c) => ({ value: c.id, label: c.name }))]}
              />
              <QuickAddButton type="client" onCreated={(c) => { setClientList((s) => [...s, c]); set("client_id", c.id); }} />
            </div>
          </Field>
          )}
          {!directSale && (
          <Field label="Supplier">
            <div className="flex items-center gap-2">
              <SearchableSelect
                testid="txn-supplier"
                placeholder="Select supplier"
                value={f.supplier_id || "none"}
                onValueChange={(v) => set("supplier_id", v)}
                options={[{ value: "none", label: "— None —" }, ...supplierList.map((s) => ({ value: s.id, label: s.name }))]}
              />
              <QuickAddButton type="supplier" onCreated={(s) => { setSupplierList((prev) => [...prev, s]); set("supplier_id", s.id); }} />
            </div>
          </Field>
          )}

          <div className="col-span-2 mt-2 border-b border-gray-100 pb-1 lx-label">Commercial Terms</div>
          <Field label="Currency">
            <CurrencySelect value={f.currency || "TZS"} onValueChange={(v) => set("currency", v)} testid="txn-currency" />
          </Field>
          {f.currency && f.currency !== "TZS" && (
            <Field label={`Exchange Rate (${f.currency}→TZS)`}><Input type="number" data-testid="txn-exchange" value={f.exchange_rate || ""} onChange={(e) => set("exchange_rate", e.target.value)} /><div className="mt-1 text-[11px] text-gray-400">Auto-filled from latest daily rate — override if needed.</div></Field>
          )}

          <Field label="Assigned To">
            <Select value={f.assigned_to || "none"} onValueChange={(v) => set("assigned_to", v)}>
              <SelectTrigger data-testid="txn-assigned"><SelectValue placeholder="Assign" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role_code}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={f.status || "Quote Requested"} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="txn-status"><SelectValue /></SelectTrigger>
              <SelectContent>{(f.status && !STATUS_FLOW.includes(f.status) ? [f.status, ...STATUS_FLOW] : STATUS_FLOW).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>

          <Field label="ETA"><DateField data-testid="txn-eta" value={(f.eta || "").slice(0, 10)} onChange={(e) => set("eta", e.target.value)} /></Field>

          {!directSale && (
          <Field label="Procurement Type (Local / Import)">
            <Select value={f.is_import ? "Import" : "Local"} onValueChange={(v) => set("is_import", v === "Import")}>
              <SelectTrigger data-testid="txn-local-import"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Local">Local Purchase</SelectItem>
                <SelectItem value="Import">Import</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          )}
          {!directSale && f.is_import && (
            <Field label="Shipment Mode">
              <Select value={f.shipment_mode || "Sea"} onValueChange={(v) => set("shipment_mode", v)}>
                <SelectTrigger data-testid="txn-shipment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{["Air", "Sea", "Road"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          {directSale
            ? <div className="col-span-2 -mt-1 rounded-sm bg-orange-50 px-2 py-1 text-[11px] text-orange-700">Selling from existing stock — supplier/procurement steps are skipped. Each line is checked against available stock.</div>
            : <div className="col-span-2 -mt-1 text-[11px] text-gray-400">Stocking vs client-allocation is now set per line item below.</div>}
        </div>

        {/* Line items */}
        <div className="mt-2">
          <div className="mb-2 flex items-center justify-between">
            <span className="lx-label">Line Items</span>
            <Button size="sm" variant="outline" data-testid="add-line-item" onClick={() => set("line_items", [...(f.line_items || []), emptyItem()])}><Plus size={14} className="mr-1" />Add</Button>
          </div>
          <div className="space-y-2">
            {(f.line_items || []).map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2" data-testid={`line-item-${i}`}>
                <Input className="col-span-4" placeholder="Description" data-testid={`li-desc-${i}`} value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} />
                <div className="col-span-2">
                  <Input type="number" placeholder="Qty" data-testid={`li-qty-${i}`} value={it.qty} onChange={(e) => {
                    let v = e.target.value;
                    const rem = remainingFor(it);
                    if (rem != null && Number(v) > rem + 1e-6) { toast.warning(`Only ${rem} remaining on this Client PO line`); v = rem; }
                    setItem(i, "qty", v);
                  }} />
                  {(() => {
                    const rem = remainingFor(it);
                    if (!directSale && !it.stocking && !it.cost_only && it.client_po_no && rem != null) {
                      const over = (Number(it.qty) || 0) > rem + 1e-6;
                      return <div className={`mt-0.5 text-[10px] ${over ? "text-red-500" : "text-gray-400"}`} data-testid={`li-remaining-${i}`}>Max {fmtNum(rem)} left on PO {it.client_po_no}</div>;
                    }
                    return null;
                  })()}
                </div>
                <Input className="col-span-2" type="number" placeholder="Unit" data-testid={`li-unit-${i}`} value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
                <div className="col-span-2">
                  <Select value={it.tax_type || "Vatable"} onValueChange={(v) => setItem(i, "tax_type", v)}>
                    <SelectTrigger className="h-9 text-xs" data-testid={`li-tax-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["Vatable", "Zero-Rated", "Exempt"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input className="col-span-1 lx-num" readOnly value={fmtNum(it.exclusive_amount, f.currency)} />
                <button className="col-span-1 flex items-center justify-center text-gray-400 hover:text-red-500" data-testid={`li-remove-${i}`} onClick={() => set("line_items", f.line_items.filter((_, j) => j !== i))}><Trash size={16} /></button>
                <div className="col-span-12 grid grid-cols-12 gap-2">
                  {!directSale && (
                  <div className="col-span-4">
                    <Select value={it.cost_only ? "Cost" : it.stocking ? "Stocking" : "Procuring"} onValueChange={(v) => {
                      const items = [...f.line_items];
                      if (v === "Cost") {
                        items[i] = { ...items[i], cost_only: true, stocking: false, client_po_no: "", client_po_id: null, item_id: null, sku: null };
                      } else {
                        const stk = v === "Stocking";
                        items[i] = { ...items[i], cost_only: false, stocking: stk, ...(stk ? { client_po_no: "", client_po_id: null } : {}) };
                      }
                      set("line_items", items);
                    }}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`li-mode-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Procuring">Procuring (client PO)</SelectItem>
                        <SelectItem value="Stocking">Stocking (Somcore stock)</SelectItem>
                        <SelectItem value="Cost">Cost only (landed, non-stock)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  )}
                  {!directSale && !it.stocking && !it.cost_only && (
                    <div className="col-span-4">
                      <SearchableSelect
                        testid={`li-cpo-${i}`}
                        className="h-8 text-xs"
                        placeholder="Client PO"
                        value={it.client_po_no || "none"}
                        onValueChange={(v) => {
                          const list = [...f.line_items];
                          if (v === "none") { list[i] = { ...list[i], client_po_no: "", client_po_id: null, item_id: null, sku: null, client_po_line_index: null }; }
                          else {
                            const po = clientPos.find((p) => p.client_po_no === v);
                            list[i] = { ...list[i], client_po_no: v, client_po_id: po?.id || null, item_id: null, sku: null, client_po_line_index: null };
                            if (po?.id) loadPoDetail(po.id);
                          }
                          set("line_items", list);
                        }}
                        options={[{ value: "none", label: "— Select client PO —" }, ...clientPos
                          .filter((p) => p.client_po_no && (p.fulfillment_status !== "Completed" || p.client_po_no === it.client_po_no))
                          .map((p) => ({ value: p.client_po_no, label: p.client_name ? `${p.client_po_no} · ${p.client_name}` : p.client_po_no }))]}
                      />
                    </div>
                  )}
                  {it.cost_only ? (
                    <div className="col-span-8 flex items-center rounded-sm bg-amber-50 px-2 text-[11px] text-amber-700" data-testid={`li-cost-note-${i}`}>Absorbed as landed cost across the stock items on this PO — not stocked, not billed to the client.</div>
                  ) : (
                  <div className={directSale ? "col-span-9" : (it.stocking ? "col-span-8" : "col-span-4")}>
                    <div className="flex items-center gap-1">
                      <SearchableSelect
                        testid={`li-sku-${i}`}
                        className="h-8 text-xs"
                        placeholder={(directSale || it.stocking) ? "Select SKU (Somcore inventory)" : (it.client_po_id ? "Select item on this PO" : "Select client PO first")}
                        value={it.item_id || "none"}
                        onValueChange={async (v) => {
                          if (v === "none") { setItem(i, "item_id", null); return; }
                          const lst = [...f.line_items];
                          if (directSale || it.stocking) {
                            const it2 = items.find((x) => x.id === v);
                            lst[i] = { ...lst[i], item_id: v, sku: it2?.sku || lst[i].sku };
                            set("line_items", lst);
                            if (it.stocking && !Number(lst[i].unit_price)) {
                              try {
                                const r = await api.get(`/items/${v}/last-cost`);
                                if (r.data?.unit_price) {
                                  const price = Number(r.data.unit_price);
                                  const cur = [...f.line_items];
                                  cur[i] = { ...cur[i], item_id: v, sku: it2?.sku || cur[i].sku, unit_price: price, exclusive_amount: (Number(cur[i].qty) || 0) * price };
                                  set("line_items", cur);
                                  toast.info(`Filled last purchase cost ${fmtNum(price)}`);
                                }
                              } catch { /* no prior purchase cost on record */ }
                            }
                            return;
                          } else {
                            const opt = skuOptionsFor(it).find((o) => o.value === v);
                            const l = opt?.poLine; const im = opt?.im;
                            const price = (Number(lst[i].unit_price) || 0) === 0 && l?.unit_price ? l.unit_price : lst[i].unit_price;
                            const rem = l?.qty_remaining ?? null;
                            let qty = Number(lst[i].qty) || 1;
                            if (rem != null && qty > rem) qty = rem;
                            lst[i] = { ...lst[i], item_id: im?.id || l?.item_id || null, sku: l?.sku || im?.sku || null,
                              client_po_line_index: l?.line_index ?? null,
                              description: lst[i].description || l?.description || im?.name || "",
                              unit_price: price, qty,
                              exclusive_amount: (Number(qty) || 0) * (Number(price) || 0) };
                          }
                          set("line_items", lst);
                        }}
                        options={(directSale || it.stocking)
                          ? [{ value: "none", label: "— No SKU —" }, ...items.map((x) => ({ value: x.id, label: `${x.sku} · ${x.name}` }))]
                          : [{ value: "none", label: "— Select item —" }, ...skuOptionsFor(it).map((o) => ({ value: o.value, label: o.label }))]}
                      />
                      <QuickAddButton type="item" size="sm" onCreated={(newItem) => {
                        setItems((s) => [...s, newItem]);
                        const lst = [...f.line_items];
                        lst[i] = { ...lst[i], item_id: newItem.id, sku: newItem.sku, description: lst[i].description || newItem.name };
                        set("line_items", lst);
                      }} />
                    </div>
                  </div>
                  )}
                  {directSale && (
                    <div className="col-span-3 flex items-center" data-testid={`li-avail-${i}`}>
                      {it.sku ? (
                        <span className={`rounded-sm px-2 py-1 text-[11px] font-medium ${(Number(it.qty) || 0) > (availBySku[it.sku] ?? 0) + 0.001 ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>
                          Avail: {fmtNum(availBySku[it.sku] ?? 0)}
                        </span>
                      ) : <span className="text-[11px] text-gray-400">Pick a SKU</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end gap-8 text-sm">
            <div><span className="lx-label">Exclusive</span> <span className="lx-num ml-2">{fmtNum(exclusiveTotal, f.currency)}</span></div>
            <div><span className="lx-label">VAT</span> <span className="lx-num ml-2">{fmtNum(vatAmount, f.currency)}</span></div>
            <div><span className="lx-label">Total</span> <span className="lx-num ml-2 font-bold text-[#1B4383]">{fmtNum(total, f.currency)}</span></div>
          </div>
          {f.currency && f.currency !== "TZS" && Number(f.exchange_rate) > 0 && (
            <div className="mt-1 flex justify-end text-xs text-gray-500" data-testid="txn-tzs-equivalent">≈ TSh {fmtNum(total * Number(f.exchange_rate))} @ {fmtNum(Number(f.exchange_rate))}</div>
          )}
        </div>

        <Field label="Notes"><Textarea data-testid="txn-notes" value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} /></Field>

      </>
  );

  return (
    <>
    {asPage ? (
      <div className="space-y-2" data-testid="txn-form">
        {body}
        <div className="mt-4 flex justify-end gap-2 border-t border-gray-100 pt-4">{footer}</div>
      </div>
    ) : (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="txn-form">
          <DialogHeader><DialogTitle className="font-display">{title} <span className={`ml-2 rounded-full px-2 py-0.5 align-middle text-[10px] font-semibold ${poKind === "GOODS" ? "bg-[#E6ECF5] text-[#1B4383]" : "bg-amber-100 text-amber-800"}`} data-testid="po-kind-badge">{poKind}</span></DialogTitle></DialogHeader>
          {body}
          <DialogFooter>{footer}</DialogFooter>
        </DialogContent>
      </Dialog>
    )}
        <DocumentPreview
          open={preview} onOpenChange={setPreview}
          title={directSale ? "Direct Sale" : "Somcore PO"}
          onConfirm={async () => { if (await save()) setPreview(false); }}
          meta={[
            ["Somcore PO No", f.lx_po_no || "(auto)"],
            ["Type", directSale ? "Direct Sale" : "Procurement"],
            ["Client", (clientList.find((c) => c.id === f.client_id) || {}).name],
            ["Currency", f.currency || "TZS"],
            ["VAT (18% on vatable lines)", `${curSym(f.currency || "TZS")} ${vatAmount.toLocaleString()}`],
            ["Status", f.status],
          ]}
          columns={[
            { key: "item", label: "Item" }, { key: "mode", label: "Type" },
            { key: "qty", label: "Qty", align: "right" }, { key: "price", label: "Unit Price", align: "right" },
            { key: "amt", label: "Amount (Excl.)", align: "right" },
          ]}
          rows={(f.line_items || []).map((it) => ({
            item: it.description || it.sku || "—",
            mode: directSale ? "Sale" : it.cost_only ? "Cost only" : it.stocking ? "Stocking" : "Procuring",
            qty: fmtNum(it.qty), price: fmtNum(it.unit_price, f.currency), amt: fmtNum(it.exclusive_amount, f.currency),
          }))}
          totals={[["Exclusive", fmtNum(exclusiveTotal, f.currency)], ["VAT", fmtNum(vatAmount, f.currency)], ["Total", fmtNum(total, f.currency), true]]}
          note={!directSale ? "'Cost only' lines are absorbed as landed cost across stock items and are not billed to the client." : undefined}
        />
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="lx-label">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
